import { ResourceFile } from "../types";

// Determine the resource type based on file URL and type
export function determineResourceType(fileUrl: string | null, fileType: string | null): 'pdf' | 'video' | 'quiz' | 'free_response' | 'drag_drop' | 'linking' | 'unknown' {
  if (!fileUrl && !fileType) return 'unknown';
  
  // First check explicit file types
  if (fileType === 'quiz') return 'quiz';
  if (fileType === 'free_response') return 'free_response';
  if (fileType === 'drag_drop') return 'drag_drop';
  if (fileType === 'linking') return 'linking';
  if (fileType === 'pdf') return 'pdf';
  if (fileType === 'video' || fileType === 'mp4' || fileType === 'webm' || fileType === 'mov') return 'video';
  
  if (!fileUrl) return 'unknown';

  // Check if URL contains .pdf regardless of case
  if (fileUrl.toLowerCase().includes('.pdf')) {
    console.log('Detected PDF file in URL:', fileUrl);
    return 'pdf';
  }
  
  // Enhanced video detection
  const urlLower = fileUrl.toLowerCase();
  
  // Always treat files from Supabase with specific names as videos
  if (urlLower.includes('supabase.co/storage')) {
    if (urlLower.includes('.mp4') || 
        urlLower.includes('/mp4') || 
        urlLower.includes('/5094134-hd_1920_1080_30fps') || // Specific file detection
        urlLower.includes('.webm') || 
        urlLower.includes('.mov')) {
      console.log('Detected video in Supabase URL:', fileUrl);
      return 'video';
    }
  }
  
  // Check for video extensions in the URL
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
  
  // Check for direct extension matches
  for (const ext of videoExtensions) {
    if (urlLower.includes(`.${ext}`)) {
      console.log(`Detected video file with extension .${ext} in URL: ${fileUrl}`);
      return 'video';
    }
  }
  
  // Check for video hosting services
  if (
    urlLower.includes('youtube.com') || 
    urlLower.includes('youtu.be') || 
    urlLower.includes('vimeo.com') ||
    urlLower.includes('wistia.com')
  ) {
    console.log('Detected video from hosting service URL:', fileUrl);
    return 'video';
  }
  
  console.log('Unable to determine resource type from URL:', fileUrl);
  return 'unknown';
}

// Check if the file is a PDF
export function isPdfFile(fileUrl: string | null): boolean {
  if (!fileUrl) return false;
  return fileUrl.toLowerCase().includes('.pdf');
}

/**
 * Advanced video detection for special cases and Supabase storage
 * @param fileUrl The URL to check for video patterns
 * @returns Boolean indicating if the URL is a special case video
 */
export function isSpecialCaseVideo(fileUrl: string | null): boolean {
  if (!fileUrl) return false;
  
  const urlLower = fileUrl.toLowerCase();
  
  // Special case for test videos with specific patterns
  if (urlLower.includes('5094134-hd_1920_1080_30fps') || 
      urlLower.includes('test-content')) {
    console.log('isSpecialCaseVideo: Detected special test video file:', fileUrl);
    return true;
  }
  
  // Special handling for Supabase storage videos
  if (urlLower.includes('supabase.co/storage')) {
    if (urlLower.includes('.mp4') || 
        urlLower.includes('/mp4') || 
        urlLower.includes('/5094134-hd') ||
        urlLower.includes('.webm') || 
        urlLower.includes('.mov')) {
      console.log('isSpecialCaseVideo: Detected video in Supabase URL:', fileUrl);
      return true;
    }
  }
  
  return false;
}

// Check if the file is a video with enhanced detection
export function isVideoFile(fileUrl: string | null): boolean {
  if (!fileUrl) return false;
  
  // First check our special case detection
  if (isSpecialCaseVideo(fileUrl)) {
    return true;
  }
  
  const urlLower = fileUrl.toLowerCase();
  
  // Check for video extensions
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
  for (const ext of videoExtensions) {
    if (urlLower.includes(ext)) {
      return true;
    }
  }
  
  // Check for video hosting services
  if (
    urlLower.includes('youtube.com') || 
    urlLower.includes('youtu.be') || 
    urlLower.includes('vimeo.com') ||
    urlLower.includes('wistia.com')
  ) {
    return true;
  }
  
  return false;
}

// Check if the file is a quiz
export function isQuizFile(file: ResourceFile): boolean {
  return file.file_type === 'quiz' || file.resourceType === 'quiz';
}

// Check if the file is a free response
export function isFreeResponseFile(file: ResourceFile): boolean {
  return file.file_type === 'free_response' || file.resourceType === 'free_response';
}

// Check if the file is a drag and drop
export function isDragDropFile(file: ResourceFile): boolean {
  return file.file_type === 'drag_drop' || file.resourceType === 'drag_drop';
}

// Check if the file is a linking game
export function isLinkingFile(file: ResourceFile): boolean {
  return file.file_type === 'linking' || file.resourceType === 'linking';
}

// Compatibility aliases
export const isPDF = isPdfFile;
export const isVideo = isVideoFile;
export const isQuiz = isQuizFile;
export const isFreeResponse = isFreeResponseFile;
export const isDragDrop = isDragDropFile;
export const isLinking = isLinkingFile;
